import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Compass } from "lucide-react";
import { internalLinksFor } from "@/lib/blog-internal-links";

/**
 * Renders the topic-cluster internal links for an article: a link up to the
 * cluster pillar plus curated sideways links, all with descriptive anchor text.
 */
export function InternalLinkCluster({ slug }: { slug: string }) {
  const placement = internalLinksFor(slug);
  if (!placement) return null;

  const { cluster, isPillar, pillarLink, links } = placement;
  if (!pillarLink && links.length === 0) return null;

  return (
    <nav
      aria-label="Related guides in this topic"
      className="mt-12 rounded-3xl border border-border bg-card/40 p-6 sm:p-8"
    >
      <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
        <Compass className="h-4 w-4" aria-hidden />
        {cluster.label}
      </p>

      <h2 className="mt-3 font-display text-xl font-semibold text-foreground sm:text-2xl">
        {isPillar ? "Go deeper on this topic" : "Continue reading"}
      </h2>

      <ul className="mt-5 space-y-3">
        {pillarLink && (
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: pillarLink.slug }}
              className="group inline-flex items-start gap-2 text-base font-medium text-foreground underline-offset-4 hover:text-primary hover:underline"
            >
              <ArrowUpRight
                className="mt-1 h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
              <span>
                Start with the full guide to {pillarLink.anchor}
              </span>
            </Link>
          </li>
        )}

        {links.map((l) => (
          <li key={l.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: l.slug }}
              className="group inline-flex items-start gap-2 text-base text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
            >
              <ArrowUpRight
                className="mt-1 h-4 w-4 shrink-0 text-primary/70 transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
              <span>Read about {l.anchor}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Small "part of" line shown under the article hero on spoke pages. */
export function ClusterBreadcrumbNote({ slug }: { slug: string }) {
  const placement = internalLinksFor(slug);
  if (!placement || placement.isPillar || !placement.pillarLink) return null;

  return (
    <p className="mt-6 text-sm text-muted-foreground">
      Part of our guide to{" "}
      <Link
        to="/blog/$slug"
        params={{ slug: placement.pillarLink.slug }}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {placement.pillarLink.anchor}
      </Link>
      .
    </p>
  );
}
